import React, { useEffect, useState } from "react";
import { api, dateFormat } from "../../lib/api";
import { AlertTriangle, Plus, ShieldCheck, Mail, Phone, MapPin } from "lucide-react";

const TYP_INFO = {
  fehler: { icon: AlertTriangle, label: "Ticketfall", color: "text-rose-600 bg-rose-50" },
  erweiterung: { icon: Plus, label: "Erweiterungsfall", color: "text-falke-orange bg-orange-50" },
  garantie: { icon: ShieldCheck, label: "Garantiefall", color: "text-falke-blue bg-blue-50" },
};

const STATUS_LABELS = { neu: "Neu", in_bearbeitung: "In Bearbeitung", erledigt: "Erledigt" };
const ROLE_LABELS = { admin: "Admin", vertriebler: "Vertrieb", buchhalter: "Buchhaltung" };

export default function ServiceAdmin() {
  const [items, setItems] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [typFilter, setTypFilter] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (typFilter) params.typ = typFilter;
    api.get("/admin/service-tickets", { params }).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [statusFilter, typFilter]);
  useEffect(() => { api.get("/admin/service-tickets/assignees").then((r) => setAssignees(r.data)); }, []);

  const update = async (tid, patch, successMsg) => {
    try {
      await api.patch(`/admin/service-tickets/${tid}`, patch);
      setMsg(successMsg || "Gespeichert.");
      setTimeout(() => setMsg(""), 3000);
      load();
    } catch (e) {
      setMsg("Fehler beim Speichern.");
    }
  };

  return (
    <div className="space-y-5" data-testid="service-admin-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-falke-blue">Service-Fälle</h1>
        <p className="text-falke-anthracite/70 mt-1">Garantie-, Ticket- und Erweiterungsfälle bearbeiten und zuweisen.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2" data-testid="status-filter">
          {[["", "Alle"], ["neu", "Neu"], ["in_bearbeitung", "In Bearbeitung"], ["erledigt", "Erledigt"]].map(([val, label]) => (
            <button key={val} onClick={() => setStatusFilter(val)} data-testid={`filter-status-${val || "alle"}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${statusFilter === val ? "bg-falke-blue text-white" : "bg-white border border-falke-blue/15 text-falke-blue hover:border-falke-blue/40"}`}>
              {label}
            </button>
          ))}
        </div>
        <select value={typFilter} onChange={(e) => setTypFilter(e.target.value)} data-testid="filter-typ"
          className="px-4 py-2 rounded-full text-sm border border-falke-blue/15 bg-white text-falke-blue">
          <option value="">Alle Typen</option>
          <option value="garantie">Garantiefall</option>
          <option value="fehler">Ticketfall</option>
          <option value="erweiterung">Erweiterungsfall</option>
        </select>
        {msg && <span className="text-sm text-falke-success font-semibold" data-testid="save-msg">{msg}</span>}
      </div>

      {items.length === 0 ? (
        <div className="falke-card p-10 text-center text-falke-anthracite/60" data-testid="no-tickets">Keine Service-Fälle gefunden.</div>
      ) : (
        <div className="space-y-3">
          {items.map((t) => {
            const info = TYP_INFO[t.typ] || TYP_INFO.fehler;
            const Icon = info.icon;
            return (
              <div key={t.id} className="falke-card p-5" data-testid={`admin-ticket-${t.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-[260px]">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${info.color}`}><Icon size={18} /></div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-heading text-lg text-falke-blue">{info.label}</span>
                        <span className={`status-badge ${t.status === "erledigt" ? "bg-green-100 text-green-800" : t.status === "in_bearbeitung" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>{STATUS_LABELS[t.status] || t.status}</span>
                      </div>
                      <div className="text-sm font-semibold text-falke-anthracite mt-1">{t.kunde_name}</div>
                      <div className="text-xs text-falke-anthracite/60 space-y-0.5 mt-1">
                        {t.kunde_email && <div className="flex items-center gap-1.5"><Mail size={12} /> {t.kunde_email}</div>}
                        {t.kunde_telefon && <div className="flex items-center gap-1.5"><Phone size={12} /> {t.kunde_telefon}</div>}
                        {t.kunde_anlagenadresse && <div className="flex items-center gap-1.5"><MapPin size={12} /> {t.kunde_anlagenadresse}</div>}
                        <div>Erstellt: {dateFormat(t.erstellt_am)}{t.geschlossen_am && ` · Erledigt: ${dateFormat(t.geschlossen_am)}`}</div>
                      </div>
                      <div className="text-sm mt-2 whitespace-pre-wrap">{t.beschreibung}</div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 w-full sm:w-56">
                    <label className="text-xs font-medium text-falke-blue/70">Status</label>
                    <select value={t.status} onChange={(e) => update(t.id, { status: e.target.value }, "Status aktualisiert.")} data-testid={`ticket-status-${t.id}`}
                      className="px-3 py-2 rounded-xl border border-falke-blue/15 text-sm bg-white">
                      <option value="neu">Neu</option>
                      <option value="in_bearbeitung">In Bearbeitung</option>
                      <option value="erledigt">Erledigt</option>
                    </select>
                    <label className="text-xs font-medium text-falke-blue/70">Zugewiesen an</label>
                    <select value={t.zugewiesen_an_user_id || ""} onChange={(e) => update(t.id, { zugewiesen_an_user_id: e.target.value }, e.target.value ? "Fall zugewiesen – Bearbeiter wurde benachrichtigt." : "Zuweisung entfernt.")} data-testid={`ticket-assignee-${t.id}`}
                      className="px-3 py-2 rounded-xl border border-falke-blue/15 text-sm bg-white">
                      <option value="">– Nicht zugewiesen –</option>
                      {assignees.map((a) => (
                        <option key={a.id} value={a.id}>{a.name} ({ROLE_LABELS[a.role] || a.role})</option>
                      ))}
                    </select>
                    <NotizFeld ticket={t} onSave={(notiz) => update(t.id, { notiz_intern: notiz }, "Notiz gespeichert.")} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NotizFeld({ ticket, onSave }) {
  const [val, setVal] = useState(ticket.notiz_intern || "");
  useEffect(() => { setVal(ticket.notiz_intern || ""); }, [ticket.notiz_intern]);
  const dirty = val !== (ticket.notiz_intern || "");
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-falke-blue/70">Interne Notiz</label>
      <textarea rows={2} value={val} onChange={(e) => setVal(e.target.value)} data-testid={`ticket-notiz-${ticket.id}`}
        className="px-3 py-2 rounded-xl border border-falke-blue/15 text-sm w-full" placeholder="Nur intern sichtbar…" />
      {dirty && <button onClick={() => onSave(val)} className="btn-secondary text-xs py-1.5" data-testid={`ticket-notiz-save-${ticket.id}`}>Notiz speichern</button>}
    </div>
  );
}
